import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Filter, RefreshCw } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useTimesheets,
  useMyTimesheets,
  useGenerateTimesheets,
  useApproveTimesheet,
  useRejectTimesheet,
  usePayrollSettings,
  type TimesheetFilters,
} from '@/hooks/use-payroll';
import { useAuth } from '@/contexts/auth-context';
import { usePermissions } from '@/hooks/use-permissions';
import { ApprovalError, SelfApprovedBadge, useSelfApproval } from '@/app/components/payroll/self-approval';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { toast } from '@/app/components/ui/simple-toaster';
import type { TimesheetStatus } from '@/lib/types';

// The shared TimesheetFilters type has no `person` key yet, but the list
// endpoint accepts it and useTimesheets forwards every filter it is given.
type ManagerTimesheetFilters = TimesheetFilters & { person?: number };

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

// Backend: 0=Monday ... 5=Saturday, 6=Sunday. JS getDay: Sunday=0 ... Saturday=6.
function toJsDayOfWeek(payWeekStartDay: number | undefined): number {
  if (payWeekStartDay === undefined) return 1; // Monday default
  return (payWeekStartDay + 1) % 7;
}

function formatLocalDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getWeekStart(date: Date, startDay: number): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - startDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  return formatLocalDate(d);
}

export function TimesheetsListPage() {
  const { isLoading: authLoading } = useAuth();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;

  const { data: payrollSettings } = usePayrollSettings();
  const payWeekStartDay = toJsDayOfWeek(payrollSettings?.pay_week_start_day);

  const [statusFilter, setStatusFilter] = useState<TimesheetStatus | ''>('');
  const [personFilter, setPersonFilter] = useState<PersonOption | null>(null);
  const [weekFilter, setWeekFilter] = useState(() =>
    getWeekStart(new Date(), payWeekStartDay)
  );
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const didNormalizeWeek = useRef(false);

  // Normalize the current filter to the org's pay-week start once settings load.
  useEffect(() => {
    if (payrollSettings && !didNormalizeWeek.current) {
      didNormalizeWeek.current = true;
      setWeekFilter((current) =>
        getWeekStart(new Date(current + 'T00:00:00'), payWeekStartDay)
      );
    }
  }, [payrollSettings, payWeekStartDay]);

  const managerFilters: ManagerTimesheetFilters = {
    status: statusFilter || undefined,
    week: weekFilter || undefined,
    person: isManager ? (personFilter?.person_id ?? undefined) : undefined,
  };
  const timesheetsQuery = useTimesheets(managerFilters);

  const myTimesheetsQuery = useMyTimesheets(
    { status: statusFilter || undefined },
    { enabled: !isManager && !authLoading }
  );

  const generateTimesheets = useGenerateTimesheets();
  const approveTimesheet = useApproveTimesheet();
  const rejectTimesheet = useRejectTimesheet();
  const selfApproval = useSelfApproval('timesheet');

  const allTimesheets = timesheetsQuery.data ?? [];
  const myTimesheets = myTimesheetsQuery.data ?? [];
  const displayTimesheets = isManager ? allTimesheets : myTimesheets;
  const isLoading = isManager ? timesheetsQuery.isLoading : myTimesheetsQuery.isLoading;

  const handleGenerate = () => {
    const weekStart = getWeekStart(new Date(weekFilter + 'T00:00:00'), payWeekStartDay);
    generateTimesheets.mutate(
      {
        person_id: 'all',
        week_starting: weekStart,
      },
      {
        onSuccess: (data) => {
          if ((data.failed_count ?? 0) > 0) {
            const successNote = data.created_count > 0 ? ` (${data.created_count} created successfully)` : '';
            toast.error(`Failed to generate ${data.failed_count} timesheet(s).${successNote}`);
          } else if (data.created_count > 0) {
            toast.success(`Generated ${data.created_count} timesheet(s).`);
          } else if (data.skipped_count > 0) {
            toast.info('Timesheets already exist for this week.');
          } else {
            toast.info('No time entries found for this week.');
          }
        },
        onError: (error) => {
          const msg =
            (error as { response?: { data?: { error?: string; detail?: string; week_starting?: string[] } } })
              .response?.data?.error ??
            (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ??
            (error as { response?: { data?: { week_starting?: string[] } } }).response?.data?.week_starting?.[0] ??
            'Failed to generate timesheets.';
          toast.error(msg);
        },
      },
    );
  };

  const handleApprove = (id: number) =>
    selfApproval.approve(id, (confirmSelfApproval) =>
      approveTimesheet.mutateAsync({ id, confirmSelfApproval }),
    );

  const handleReject = (id: number) => {
    if (!rejectReason.trim()) return;
    rejectTimesheet.mutate(
      { id, reason: rejectReason },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectReason('');
        },
      }
    );
  };

  const prevWeek = () => {
    const d = new Date(weekFilter + 'T00:00:00');
    d.setDate(d.getDate() - 7);
    setWeekFilter(getWeekStart(d, payWeekStartDay));
  };

  const nextWeek = () => {
    const d = new Date(weekFilter + 'T00:00:00');
    d.setDate(d.getDate() + 7);
    setWeekFilter(getWeekStart(d, payWeekStartDay));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {selfApproval.dialog}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Timesheets</h1>
          <p className="text-gray-600 mt-1">
            {isManager ? 'Manage all tech timesheets' : 'View your weekly timesheets'}
          </p>
        </div>
        {isManager && (
          <Button onClick={handleGenerate} disabled={generateTimesheets.isPending}>
            <RefreshCw className={`h-4 w-4 mr-2 ${generateTimesheets.isPending ? 'animate-spin' : ''}`} />
            Generate Timesheets
          </Button>
        )}
      </div>

      {/* Week Selector & Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prevWeek}>
                &larr;
              </Button>
              <Input
                type="date"
                value={weekFilter}
                onChange={(e) => setWeekFilter(getWeekStart(new Date(e.target.value + 'T00:00:00'), payWeekStartDay))}
                className="flex-1"
              />
              <Button variant="outline" size="sm" onClick={nextWeek}>
                &rarr;
              </Button>
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as TimesheetStatus | '')}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            {isManager && (
              <PersonSearchComboBox
                value={personFilter}
                onChange={(option) => setPersonFilter(option?.person_id != null ? option : null)}
                placeholder="Search workers to filter..."
                endpoint="persons"
                className="mt-0"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submitted Timesheets - Approval Queue (manager only) */}
      {isManager && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Approval Queue</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(() => {
              const submitted = allTimesheets.filter((t) => t.status === 'submitted');
              if (submitted.length === 0) {
                return (
                  <div className="p-6 text-center text-gray-500">
                    No timesheets pending approval
                  </div>
                );
              }
              return (
                <div className="divide-y divide-gray-200">
                  {submitted.map((ts) => (
                    <div key={ts.id} className="flex items-center justify-between p-4">
                      <div>
                        <p className="font-medium text-gray-900">{ts.person_name ?? ts.tech_name ?? 'Unknown'}</p>
                        <p className="text-sm text-gray-500">
                          Week of {ts.week_starting} &middot; {ts.total_hours}h
                          ({ts.total_regular_hours}h regular, {ts.total_overtime_hours}h OT)
                        </p>
                        <ApprovalError message={selfApproval.errorFor(ts.id)} className="mt-1" />
                      </div>
                      <div className="flex gap-2">
                        {rejectingId === ts.id ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              placeholder="Rejection reason..."
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              className="w-48"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(ts.id)}
                              disabled={rejectTimesheet.isPending}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setRejectingId(null);
                                setRejectReason('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600"
                              onClick={() => handleApprove(ts.id)}
                              disabled={approveTimesheet.isPending}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => setRejectingId(ts.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Timesheet List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {isManager ? 'All Timesheets' : 'My Timesheets'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading timesheets...</div>
          ) : displayTimesheets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No timesheets found for this week</p>
              {isManager && (
                <Button className="mt-4" variant="outline" onClick={handleGenerate}>
                  Generate Timesheets
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {displayTimesheets.map((ts) => (
                <Link
                  key={ts.id}
                  to={`/payroll/timesheets/${ts.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{ts.person_name ?? ts.tech_name ?? 'Unknown'}</p>
                    <p className="text-sm text-gray-500">
                      Week of {ts.week_starting}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold">{ts.total_hours}h</p>
                      <p className="text-xs text-gray-500">
                        {ts.total_regular_hours}h + {ts.total_overtime_hours}h OT
                      </p>
                    </div>
                    <Badge className={statusColors[ts.status] ?? 'bg-gray-100 text-gray-800'}>
                      {ts.status}
                    </Badge>
                    <SelfApprovedBadge selfApproved={ts.self_approved} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
